import React, { useState, useEffect } from 'react';
import { FiUsers, FiTrendingUp, FiActivity } from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ownerAnalyticsAPI } from '../../../api/ownerEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Card,
  ChartContainer,
  LoadingContainer,
  EmptyState,
  Badge,
} from './Analytics.styles';

const Users = ({ startDate, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { 
        start_date: startDate ? `${startDate}T00:00:00Z` : undefined,
        end_date: endDate ? `${endDate}T23:59:59Z` : undefined,
      };
      const response = await ownerAnalyticsAPI.getUsers(params);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const prepareGrowthData = () => {
    if (!data?.growth) return [];
    return data.growth.map((item) => ({
      month: item.month.split('-')[1],
      students: 0,
      teachers: 0,
      parents: 0,
    }));
  };

  const prepareDAUData = () => {
    if (!data?.daily_active_users) return [];
    return data.daily_active_users.map((item) => ({
      date: new Date(item.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      users: safeParseFloat(item.active_users),
    }));
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading user analytics..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiUsers size={64} />
        <h3>No User Data</h3>
        <p>User analytics will appear when available</p>
      </EmptyState>
    );
  }

  const dauData = prepareDAUData();
  const roles = data.roles || {};

  return (
    <>
      <Section>
        <SectionTitle>
          <FiUsers />
          User Metrics
        </SectionTitle>
        <Grid minWidth="200px">
          <StatCard>
            <StatLabel>Total Students</StatLabel>
            <StatValue>{safeParseFloat(roles.student).toLocaleString()}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Total Teachers</StatLabel>
            <StatValue>{safeParseFloat(roles.teacher).toLocaleString()}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Total Parents</StatLabel>
            <StatValue>{safeParseFloat(roles.parent).toLocaleString()}</StatValue>
          </StatCard>

          <StatCard>
            <StatLabel>Owners</StatLabel>
            <StatValue>{safeParseFloat(roles.owner).toLocaleString()}</StatValue>
          </StatCard>
        </Grid>
      </Section>

      {dauData.length > 0 && (
        <Section>
          <SectionTitle>
            <FiActivity />
            Daily Active Users (DAU)
          </SectionTitle>
          <Card>
            <ChartContainer height="300px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dauData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis dataKey="date" stroke={theme.colors.textSecondary} />
                  <YAxis stroke={theme.colors.textSecondary} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke={theme.colors.primary} 
                    strokeWidth={2}
                    dot={{ fill: theme.colors.primary }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}
    </>
  );
};

export default Users;

